import loadingCard from '@images/loading-card.svg'
import { action } from '@ember/object'
import Component from '@glimmer/component'
import type { FilterKey, PrimaryFilter } from 'teacher-ui-client/reading-scales/utils'
import { service } from '@ember/service'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import type { StartSearchParams } from 'teacher-ui-client/services/book-library/book-finder'
import { didCancel } from 'ember-concurrency'
import { clamp, isEqual } from 'lodash'
import type { SearchGroup } from 'teacher-ui-client/book-library/book-finder/utils'
import { tracked } from '@glimmer/tracking'
import { queryParam } from 'teacher-ui-client/decorators/query-param'
import type { Log } from '@blakeelearning/log'
import type BookFinderService from 'teacher-ui-client/services/book-library/book-finder'
import type BookLibraryCollectionService from 'teacher-ui-client/services/book-library/collection'
import type BookLibrarySearchRoute from 'teacher-ui-client/subscription-type/book-library/search/route'
import {
  decoalesceCategoryIds,
  type FormChangeset,
} from 'teacher-ui-client/components/book-library/additional-filters-support'
import { Changeset } from 'ember-changeset'
import { BookLibraryFilterRow } from 'teacher-ui-client/components/book-library/filter-row'
import { BookLibraryPrimaryFilterSelect } from 'teacher-ui-client/components/book-library/primary-filter-select'
import { BookLibraryPrimaryFilterSlider } from 'teacher-ui-client/components/book-library/primary-filter-slider'
import AdditionalFilters from 'teacher-ui-client/components/book-library/additional-filters'
import { InputText } from 'teacher-ui-client/components/base/input-text'
import { ContentTile } from 'teacher-ui-client/components/tiles/content'
import { CarouselScrollComponent } from 'teacher-ui-client/components/base/carousel-scroll'
import { BookLibraryBookCard } from 'teacher-ui-client/components/book-library/book-card'
import { FidgetSpinnerWaveComponent } from '@blakeelearning/fidget/components/fidget/spinner/wave'
import { doAction } from 'teacher-ui-client/modifiers/do-action'
import { hash, fn, concat } from '@ember/helper'
import { themeKey } from 'teacher-ui-client/helpers/theme-key'
import { on } from '@ember/modifier'
import { pick } from 'teacher-ui-client/helpers/pick'
import { t } from 'ember-intl'
import { and, eq } from 'ember-truth-helpers'
import inViewport from 'ember-in-viewport/modifiers/in-viewport'
import { FidgetLoadingIndicatorComponent } from '@blakeelearning/fidget/components/fidget/loading-indicator'
import { NoResultsMessage } from 'teacher-ui-client/components/no-results-message'
import type BookLibraryCollection from 'teacher-ui-client/models/book-library/collection'

type Args = ModelFor<BookLibrarySearchRoute>

export class BookLibrarySearchPage extends Component<Args> {
  @service('book-library/book-finder') bookFinder!: BookFinderService

  @service('book-library/collection') collectionService!: BookLibraryCollectionService

  @service log!: Log

  @service subscriptionType!: SubscriptionTypeService

  @queryParam('subscription-type/book-library/search') primaryFilterKey!: FilterKey

  @queryParam('subscription-type/book-library/search') primaryFilterValue!: string

  @queryParam('subscription-type/book-library/search') keywords!: string

  @queryParam('subscription-type/book-library/search') categories!: string[]

  @queryParam('subscription-type/book-library/search') withinCategories!: string[]

  @queryParam('subscription-type/book-library/search') excluded?: boolean

  get additionalFiltersState() {
    const categories = decoalesceCategoryIds(this.categories)
    const fiction = categories.filter((category) => category.startsWith('fiction'))
    const nonfiction = categories.filter((category) => category.startsWith('nonfiction'))
    return {
      fiction,
      nonfiction,
      ancillary: this.ancillaryCategories,
    }
  }

  get additionalFiltersChangeset(): FormChangeset {
    return Changeset(this.additionalFiltersState) as FormChangeset
  }

  get dependenciesLoaded() {
    return (
      this.primaryFilterKeyOrDefault && this.args.loadPrimaryFilters.isFinished && this.args.loadCollections.isFinished
    )
  }

  get loadingCardSvg() {
    return loadingCard
  }

  get primaryFilterKeyOrDefault() {
    return this.primaryFilterKey || this.primaryFilters[0]?.id
  }

  get primaryFilters(): PrimaryFilter[] {
    return this.args.loadPrimaryFilters.value ?? []
  }

  /**
   * A live array of collections, so that it updates as new ones are created
   */
  get collections(): BookLibraryCollection[] {
    return this.collectionService.allMyCollections
  }

  get currentSubscriptionType() {
    return this.subscriptionType.current
  }

  /**
   * Deserialize the primaryFilterValue from the URL into the desired 'from' value.
   * Ensure the value is valid and is within the bounds for the scale.
   */
  get primaryFilterFromValue() {
    const [fromValue] = this.primaryFilterValue.split(':')
    const { lower, upper } = this.primaryFilterBounds

    const _primaryFilterFromValue = Number.parseInt(fromValue, 10)
    return Number.isInteger(_primaryFilterFromValue) ? clamp(_primaryFilterFromValue, lower, upper) : lower
  }

  /**
   * Serialize the 'from' value into the primaryFilterValue for the URL
   */
  set primaryFilterFromValue(fromValue) {
    // Ensure the to value is never less than the from value
    let toValue = this.primaryFilterToValue
    if (toValue < fromValue) toValue = fromValue

    this.primaryFilterValue = [fromValue, toValue].join(':')
  }

  /**
   * Deserialize the primaryFilterValue from the URL into the desired 'to' value.
   * Ensure the value is valid and is within the bounds for the scale.
   */
  get primaryFilterToValue() {
    const [, toValue] = this.primaryFilterValue.split(':')
    const { lower, upper } = this.primaryFilterBounds

    const _primaryFilterToValue = Number.parseInt(toValue, 10)
    return Number.isInteger(_primaryFilterToValue) ? clamp(_primaryFilterToValue, lower, upper) : upper
  }

  /**
   * Serialize the 'to' value into the primaryFilterValue for the URL
   */
  set primaryFilterToValue(toValue) {
    this.primaryFilterValue = [this.primaryFilterFromValue, toValue].join(':')
  }

  get currentPrimaryFilter() {
    const { primaryFilters, primaryFilterKeyOrDefault } = this
    return primaryFilters.find((filter: PrimaryFilter) => filter.id === primaryFilterKeyOrDefault) ?? primaryFilters[0]
  }

  /**
   * @return {[Number, Number]}
   */
  get primaryFilterBounds() {
    const primaryFilterOptionIndices = this.currentPrimaryFilter.options.map((_option, index) => index) || []
    const lower = 0
    const upper = Math.max(primaryFilterOptionIndices.length - 1, 0) // in case of empty array
    return { lower, upper }
  }

  get keywordsInputValue() {
    return this.keywords
  }

  get primaryFilterSliderFromLabels() {
    return this.currentPrimaryFilter.options.map((option) => {
      return option.rangeLabels?.min ?? option.label ?? ''
    })
  }

  get primaryFilterSliderToLabels() {
    return this.currentPrimaryFilter.options.map((option) => {
      return option.rangeLabels?.max ?? option.label ?? ''
    })
  }

  primaryFilterValueFormatter(sliderLabels: string[], optionIndex: number) {
    return sliderLabels[optionIndex] ?? ''
  }

  // Holds an array of book groups to be searched, each containing enough info to request the necessary data & page it.
  // Each represents a panel in the UI.
  @tracked searchGroups: SearchGroup[] = []

  // Find the next search group that has no result for all of its pagers. This is the next one to load.
  get nextSearchGroup() {
    return this.searchGroups.find((searchGroup) => searchGroup.pagers.length > 0 && searchGroup.hasNoResults)
  }

  get noResults() {
    // no results if all group pagers are fulfilled & no books!
    return this.searchGroups.every((searchGroup) => searchGroup.allPagersEmpty)
  }

  @action
  async startSearch(_element: HTMLElement, searchArgs: StartSearchParams) {
    try {
      this.searchGroups = [] // clear the current search groups while waiting for the next ones to arrive
      this.searchGroups = await this.bookFinder.startSearch.perform(searchArgs)
    } catch (e: any) {
      if (!didCancel(e)) this.log.error(e)
    }
  }

  // despite the template indicating this should only be called if nextSearchGroup exists, it is sometimes undefined
  @action
  onGroupLoaderEnter(nextSearchGroup: SearchGroup | undefined) {
    nextSearchGroup?.pagers.forEach((pager) => {
      void pager.loadNextPage()
    })
  }

  /**
   * Returns an array of ancillary categories for the special additional filters.
   * This is made up of the "withinCategories", and the excluded filter.
   * For the purposes of the AdditionalFilters component, we treat excluded as ancillary category.
   */
  get ancillaryCategories() {
    if (this.excluded) {
      return [...(this.withinCategories ?? []), '*.excluded']
    }
    return this.withinCategories ?? []
  }

  @action
  onCommitAdditionalFilters(categories: string[], ancillaryCategories: string[]) {
    let withinCategories = ancillaryCategories
    let excluded = false
    if (ancillaryCategories.includes('*.excluded')) {
      excluded = true
      withinCategories = ancillaryCategories.filter((c) => c !== '*.excluded')
    }

    if (!isEqual(this.categories, categories)) {
      this.categories = categories
    }
    if (!isEqual(this.withinCategories, withinCategories)) {
      this.withinCategories = withinCategories
    }
    if (!isEqual(this.excluded, excluded)) {
      this.excluded = excluded
    }
  }

  @action
  onPrimaryFilterChange(primaryFilterKey: FilterKey) {
    this.primaryFilterKey = primaryFilterKey
    this.resetPrimaryFilterValues()
  }

  @action
  resetPrimaryFilterValues() {
    const { lower, upper } = this.primaryFilterBounds
    this.primaryFilterFromValue = lower
    this.primaryFilterToValue = upper
  }

  @action
  onPrimaryFilterFrom(value: number) {
    this.primaryFilterFromValue = value
  }

  @action
  onPrimaryFilterTo(value: number) {
    this.primaryFilterToValue = value
  }

  @action
  updateKeywords(keywords: string) {
    this.keywords = keywords
  }

  <template>
    {{#if this.dependenciesLoaded}}
      <div
        class="mb-4 sm:mx-4 md:mx-8 lg:mx-12 xl:mx-24"
        {{doAction
          this.startSearch
          (hash
            currentPrimaryFilter=this.currentPrimaryFilter
            primaryFilterFromValue=this.primaryFilterFromValue
            primaryFilterToValue=this.primaryFilterToValue
            categories=this.categories
            withinCategories=this.withinCategories
            keywords=this.keywords
            excluded=this.excluded
          )
        }}
      >
        <BookLibraryFilterRow
          data-test-filter-row-primary
          class="text-dusty-black-250 flex flex-wrap items-center justify-center px-12 pt-2"
        >
          <div class="shrink-0 basis-1/3 px-2">
            <BookLibraryPrimaryFilterSelect
              data-test-primary-filter-select
              class="w-full p-4 font-semibold uppercase"
              @condensedList={{true}}
              @label={{t "bookLibrary.pages.search.filterBy"}}
              @primaryFilters={{this.primaryFilters}}
              @currentPrimaryFilter={{this.currentPrimaryFilter}}
              @onPrimaryFilterChange={{this.onPrimaryFilterChange}}
            />
          </div>
          <BookLibraryPrimaryFilterSlider
            class="shrink-0 basis-2/3 px-2"
            @primaryFilterValues={{hash from=this.primaryFilterFromValue to=this.primaryFilterToValue}}
            @primaryFilterBounds={{this.primaryFilterBounds}}
            @primaryFilterValueFromFormatter={{fn this.primaryFilterValueFormatter this.primaryFilterSliderFromLabels}}
            @primaryFilterValueToFormatter={{fn this.primaryFilterValueFormatter this.primaryFilterSliderToLabels}}
            @onUpdateValues={{hash from=this.onPrimaryFilterFrom to=this.onPrimaryFilterTo}}
          />
        </BookLibraryFilterRow>
        <BookLibraryFilterRow data-test-filter-row-secondary class="font-dusty-black-250 flex px-12 py-2">
          <AdditionalFilters
            data-test-additional-filters-drop-down
            class="px-2"
            @changeset={{this.additionalFiltersChangeset}}
            @onCommit={{this.onCommitAdditionalFilters}}
            @theme={{themeKey this.currentSubscriptionType}}
          />
        </BookLibraryFilterRow>
        <div class="mx-4 my-6 flex justify-center">
          <InputText
            data-test-search-box-input
            type="search"
            role="searchbox"
            class="w-full sm:w-8/12 md:w-7/12 lg:w-5/12 xl:w-4/12"
            value={{this.keywordsInputValue}}
            placeholder={{t "search"}}
            aria-label={{t "search"}}
            @themeKey={{themeKey this.currentSubscriptionType}}
            @icon="search"
            @iconEnd={{true}}
            {{on "input" (pick "target.value" this.updateKeywords)}}
          />
        </div>

        {{#each this.searchGroups as |searchGroup|}}
          {{#if (and searchGroup.hasAllResults searchGroup.hasAnyBooks)}}
            <ContentTile data-test-result-panel class="group/search mt-1 mb-6">
              <h3
                data-test-result-panel-header
                {{! Colour each panel differently, looping over 4 background colours. Anchored to "group/search" }}
                {{! https://tailwindcss.com/docs/hover-focus-and-other-states#differentiating-nested-groups }}
                {{! https://tailwindcss.com/docs/hover-focus-and-other-states#arbitrary-groups }}
                class="text-dusty-black-500 group-[:nth-of-type(4n+1)]/search:bg-forest-green-50 group-[:nth-of-type(4n+2)]/search:bg-yolky-yellow-100 group-[:nth-of-type(4n+3)]/search:bg-rexy-purple-100 group-[:nth-of-type(4n+4)]/search:bg-deep-orange-50 flex w-full items-center py-3 pl-8 italic"
              >
                <span class="mr-1">
                  {{t
                    (concat "bookLibrary.primaryFilterHeaderTitle." this.primaryFilterKeyOrDefault)
                    filter=searchGroup.name
                  }}
                </span>
                <span class="ml-2 text-xl font-normal uppercase">
                  {{t "bookLibrary.components.resultPanel.groupLength" count=searchGroup.total}}
                </span>
              </h3>
              <div class="mb-8" data-test-result-panel-body>
                {{#each searchGroup.pagers as |pager|}}
                  {{#if pager.books}}
                    <p class="text-dusty-black-250 mt-4 mb-3 ml-8 font-bold">
                      {{t (concat "bookLibrary.components.resultPanel." pager.classification)}}
                    </p>
                    <CarouselScrollComponent @navButtonsOnHover={{true}}>
                      {{#each pager.books as |book index|}}
                        <BookLibraryBookCard
                          data-test-book-library-card
                          class="{{if (eq index 0) 'pr-2 pl-8' 'px-2'}}"
                          @book={{book}}
                          @fadeIn={{true}}
                          @collections={{this.collections}}
                          @linkToBookDetail={{true}}
                          @showResourcesPill={{book.bookMetadata.hasResources}}
                        />
                      {{/each}}
                      {{#unless pager.isFulfilled}}
                        <div class="inline-block" {{inViewport onEnter=pager.loadNextPage viewportSpy=true}}>
                          <img alt={{t "bookLibrary.pages.search.loading"}} src={{this.loadingCardSvg}} />
                        </div>
                      {{/unless}}
                    </CarouselScrollComponent>
                  {{/if}}
                {{/each}}
              </div>
            </ContentTile>
          {{/if}}
        {{/each}}
        {{! group loader & spinner }}
        {{#if this.bookFinder.startSearch.isRunning}}
          <FidgetSpinnerWaveComponent data-test-search-group-loader @centered={{true}} />
        {{else if (and this.nextSearchGroup this.bookFinder.continueSearch.isIdle)}}
          {{! only show loader when not currently searching a group }}
          <div {{inViewport onEnter=(fn this.onGroupLoaderEnter this.nextSearchGroup) viewportSpy=true}}></div>
          <FidgetSpinnerWaveComponent data-test-search-group-loader @centered={{true}} />
        {{else if this.nextSearchGroup}}
          <FidgetSpinnerWaveComponent data-test-search-group-loader @centered={{true}} />
        {{else if this.noResults}}
          <NoResultsMessage
            @title={{t "bookLibrary.pages.search.empty.title"}}
            @body={{t "bookLibrary.pages.search.empty.body"}}
          />
        {{/if}}
      </div>
    {{else}}
      <FidgetLoadingIndicatorComponent
        data-test-book-library-search-loading
        @show={{true}}
        @centered={{true}}
        @appearance="small"
        @overlay={{false}}
      />
    {{/if}}
  </template>
}
export default BookLibrarySearchPage
